/**
 * RPA Job Dashboard — single screen for monitoring all RPA job types.
 *
 * Shows RevolutionEHR pulls and Vision eligibility checks with
 * summary stats, filters, expandable error detail, and one-click re-run.
 */

import { useState, useEffect, useMemo, useCallback, Fragment } from 'react'
import { stripPayerIdSuffix } from '../utils/formatters'
import { scrubVendorText } from '../utils/scrubVendorText'
import { humanizeSyncError } from '../utils/humanizeSyncError'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import { toast } from 'sonner'
import {
  subscribeToRpaJobs,
  dateRangeFromPreset,
  DATE_PRESET_LABELS,
  extractRerunParams,
  rerunJob,
  type DatePreset,
} from '../services/rpaJobs'
import type { RpaJob, RpaJobStatus, Clinic } from '../types'
import { RPA_VENDORS, type RpaVendorId } from '../../shared/rpaRegistry'
import { IconCheckCircle, IconXCircle, IconAlertTriangle, IconClock, IconRefresh, IconChevronDown, IconChevronRight } from './Icons'

/**
 * Type filter on the RPA Jobs table:
 *   - 'all'       → everything
 *   - 'revolution'→ RevolutionEHR pulls only
 *   - RpaVendorId → one per registered RPA vendor (VSP, EyeMed, …)
 * The vendor values come from the shared registry so adding a new RPA
 * vendor surfaces a new filter button automatically.
 */
type TypeFilter = 'all' | 'revolution' | RpaVendorId

// ── Props ───────────────────────────────────────────────────────────

interface RpaJobDashboardProps {
  orgId: string
  clinicId: string | null
  isAdmin: boolean
  role: string
  clinics: Clinic[]
}

// ── Job type badge ──────────────────────────────────────────────────
// For `vision` jobs, the label comes from the job's vendor context so VSP
// and EyeMed render distinctly. Revolution jobs always use the static label.

function JobTypeBadge({ job }: { job: RpaJob }) {
  const label = job.jobType === 'revolution'
    ? 'RevolutionEHR'
    : job.context.kind === 'eligibility'
      ? job.context.rpaVendorDisplay
      : 'RPA'
  return (
    <span className={`rpa-job-type rpa-job-type--${job.jobType}`}>
      {label}
    </span>
  )
}

// ── Status badge ────────────────────────────────────────────────────

function StatusBadge({ status }: { status: RpaJobStatus }) {
  switch (status) {
    case 'waiting':
      // The run or check is queued for its scraper lane but the worker
      // hasn't started the task yet. Visually distinct from In Progress so
      // users can tell whether the wait is on the queue or on the portal.
      return <span className="rpa-status rpa-status--waiting"><IconClock /> Waiting</span>
    case 'in_progress':
      return <span className="rpa-status rpa-status--in-progress"><IconClock /> In Progress</span>
    case 'success':
      return <span className="rpa-status rpa-status--success"><IconCheckCircle /> Success</span>
    case 'error':
      return <span className="rpa-status rpa-status--error"><IconXCircle /> Error</span>
    case 'timeout':
      return <span className="rpa-status rpa-status--timeout"><IconAlertTriangle /> Timeout</span>
  }
}

// ── Duration formatter ──────────────────────────────────────────────

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000)
  if (totalSec < 60) return `${totalSec}s`
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return sec > 0 ? `${min}m ${sec}s` : `${min}m`
}

// ── Date formatter ──────────────────────────────────────────────────

function formatDateTime(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function formatDob(dob: string): string {
  if (dob.includes('-')) {
    const [y, m, d] = dob.split('-')
    return `${m}/${d}/${y}`
  }
  return dob
}

// ── Sort helpers ────────────────────────────────────────────────────

type SortField = 'initiatedAt' | 'completedAt' | 'durationMs' | 'jobType' | 'status'
type SortDir = 'asc' | 'desc'

// Waiting and In Progress both represent in-flight work, so they sort to
// the top together — Waiting first since the wait is the part the user is
// typically asking "what's happening?" about.
const STATUS_RANK: Record<RpaJobStatus, number> = {
  waiting: 0,
  in_progress: 1,
  timeout: 2,
  error: 3,
  success: 4,
}

function sortJobs(jobs: RpaJob[], field: SortField, dir: SortDir): RpaJob[] {
  const sorted = [...jobs]
  const mul = dir === 'asc' ? 1 : -1
  sorted.sort((a, b) => {
    switch (field) {
      case 'initiatedAt': {
        // Queued jobs have no start time yet; sort them as most-recent so they
        // stay at the top of the default newest-first view. A terminal row
        // that never started orders by its completion (sortAt) instead of
        // sitting pinned there.
        const aT = a.sortAt?.getTime() ?? Infinity
        const bT = b.sortAt?.getTime() ?? Infinity
        return aT === bT ? 0 : mul * (aT < bT ? -1 : 1)
      }
      case 'completedAt': {
        const aT = a.completedAt?.getTime() ?? -1
        const bT = b.completedAt?.getTime() ?? -1
        return mul * (aT - bT)
      }
      case 'durationMs': {
        const aD = a.durationMs ?? -1
        const bD = b.durationMs ?? -1
        return mul * (aD - bD)
      }
      case 'jobType':
        return mul * a.jobType.localeCompare(b.jobType)
      case 'status':
        return mul * (STATUS_RANK[a.status] - STATUS_RANK[b.status])
    }
  })
  return sorted
}

// ── Main component ──────────────────────────────────────────────────

export default function RpaJobDashboard({ orgId, clinicId, isAdmin, role, clinics }: RpaJobDashboardProps) {
  const [jobs, setJobs] = useState<RpaJob[]>([])
  const [loading, setLoading] = useState(true)
  const [datePreset, setDatePreset] = useState<DatePreset>('last7')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [statusFilter, setStatusFilter] = useState<RpaJobStatus | 'all'>('all')
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null)
  const [rerunningIds, setRerunningIds] = useState<Set<string>>(() => new Set())
  const [sortField, setSortField] = useState<SortField>('initiatedAt')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [page, setPage] = useState(0)
  const pageSize = 25

  const dateRange = useMemo(() => dateRangeFromPreset(datePreset), [datePreset])
  const clinicsById = useMemo(() => new Map(clinics.map(c => [c.id, c])), [clinics])

  // Analytics: track dashboard view
  useEffect(() => {
    if (posthog.__loaded) {
      posthog.capture('rpa_dashboard_viewed', { role, filterDateRange: datePreset, filterJobType: typeFilter })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- one view event per page open, with the filters it opened on

  // Real-time subscription
  useEffect(() => {
    setLoading(true)
    const unsub = subscribeToRpaJobs(
      orgId, clinicId, isAdmin, dateRange,
      (data) => { setJobs(data); setLoading(false) },
    )
    return unsub
  }, [orgId, clinicId, isAdmin, dateRange])

  // Reset page when filters change
  useEffect(() => { setPage(0) }, [datePreset, typeFilter, statusFilter, sortField, sortDir])

  // Type filter applied on its own (date filter is already applied server-side
  // when fetching `jobs`). This drives BOTH the summary tiles and the table, so
  // the tile counts reflect the selected RPA type — but NOT the status filter,
  // since the tiles ARE the status filter (folding it in would zero out every
  // non-selected tile).
  const typeFilteredJobs = useMemo(() => {
    if (typeFilter === 'revolution') {
      return jobs.filter(j => j.jobType === 'revolution')
    }
    if (typeFilter !== 'all') {
      // Per-vendor filter: match eligibility jobs whose rpaVendor equals
      // the selected RpaVendorId (e.g. 'vsp', 'eyemed').
      return jobs.filter(j =>
        j.context.kind === 'eligibility' && j.context.rpaVendor === typeFilter,
      )
    }
    return jobs
  }, [jobs, typeFilter])

  // Table list: status filter applied on top of the type filter, then sorted.
  const filteredJobs = useMemo(() => {
    let result = typeFilteredJobs
    if (statusFilter !== 'all') {
      // The In Progress tile is a meta-filter that includes Waiting jobs too,
      // since the tile count already folds them together. All other tiles
      // map 1:1 to a single normalized status.
      if (statusFilter === 'in_progress') {
        result = result.filter(j => j.status === 'in_progress' || j.status === 'waiting')
      } else {
        result = result.filter(j => j.status === statusFilter)
      }
    }
    return sortJobs(result, sortField, sortDir)
  }, [typeFilteredJobs, statusFilter, sortField, sortDir])

  const totalPages = Math.ceil(filteredJobs.length / pageSize)
  const pagedJobs = filteredJobs.slice(page * pageSize, (page + 1) * pageSize)

  // Summary stats (computed from the type-filtered list, ignoring the status
  // filter). Waiting jobs are folded into the In Progress tile count to keep
  // the tile layout stable — the badge in each row still distinguishes the two
  // states.
  const stats = useMemo(() => {
    let inProgress = 0, succeeded = 0, errored = 0, timedOut = 0
    for (const j of typeFilteredJobs) {
      switch (j.status) {
        case 'waiting':
        case 'in_progress': inProgress++; break
        case 'success': succeeded++; break
        case 'error': errored++; break
        case 'timeout': timedOut++; break
      }
    }
    return { inProgress, succeeded, errored, timedOut, total: typeFilteredJobs.length }
  }, [typeFilteredJobs])

  // The In Progress tile filters to BOTH in_progress and waiting (matches
  // the stats fold-in). Other tiles use simple equality.
  function handleTileClick(status: RpaJobStatus) {
    setStatusFilter(prev => prev === status ? 'all' : status)
  }

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir(prev => prev === 'desc' ? 'asc' : 'desc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const canRerun = (job: RpaJob) => job.status === 'error' || job.status === 'timeout'

  const handleRerun = useCallback(async (job: RpaJob) => {
    if (rerunningIds.has(job.id)) return
    setRerunningIds(prev => new Set(prev).add(job.id))
    if (posthog.__loaded) {
      posthog.capture('rpa_job_rerun', {
        jobType: job.jobType,
        originalStatus: job.status,
        originalJobId: job.sourceDocId,
        role,
      })
    }

    try {
      const rerun = extractRerunParams(job, orgId)
      if (!rerun) { toast.error('Missing re-run parameters.'); return }
      const p = rerun.params
      if (!p.orgId || !p.clinicId) { toast.error('Missing org or clinic info — cannot re-run this job.'); return }
      if (rerun.fn === 'initiateVspCheck') {
        const sub = p.subscriber as Record<string, string> | undefined
        if (!sub?.firstName || !sub?.lastName || !sub?.memberId) {
          toast.error('Missing subscriber details — cannot re-run this job.')
          return
        }
      }
      const data = await rerunJob(rerun)

      // initiateAppointmentPull returns {results: PullResult[]},
      // initiateVspCheck returns {action: string} at top level.
      const results = data?.results as Array<{action: string; reason?: string}> | undefined
      const action = results?.[0]?.action ?? (data?.action as string | undefined)

      if (action === 'skipped') {
        const reason = results?.[0]?.reason ?? 'already processed'
        toast.warning(`Skipped: ${scrubVendorText(reason)}.`)
      } else if (action === 'reused') {
        toast.warning('A check for this patient is already in progress.')
      } else if (action === 'failed') {
        toast.error('Check created but the bot trigger failed. You can re-run it from this dashboard.')
      } else {
        toast.success('Job triggered successfully.')
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error(`Re-run failed: ${scrubVendorText(msg)}`)
      Sentry.captureException(err)
    } finally {
      setRerunningIds(prev => {
        const next = new Set(prev)
        next.delete(job.id)
        return next
      })
    }
  }, [orgId, role, rerunningIds])

  const toggleExpand = useCallback((job: RpaJob) => {
    const next = expandedJobId === job.id ? null : job.id
    setExpandedJobId(next)
    if (next && posthog.__loaded) {
      posthog.capture('rpa_job_detail_expanded', { jobType: job.jobType, status: job.status })
    }
  }, [expandedJobId])

  function sortArrow(field: SortField) {
    if (sortField !== field) return null
    return <span className="sort-arrow">{sortDir === 'desc' ? ' ↓' : ' ↑'}</span>
  }

  function thClass(field: SortField) {
    return `sortable-th${sortField === field ? ' sortable-th--active' : ''}`
  }

  return (
    <main className="dashboard-main">
      <div className="rpa-dashboard">
        {/* Header */}
        <h2 className="rpa-dashboard__title">Data Syncs</h2>
        <p className="rpa-dashboard__subtitle">
          Monitor RevolutionEHR pulls and Vision eligibility checks
          {!loading && <> &middot; {stats.total} job{stats.total !== 1 ? 's' : ''}</>}
        </p>

        {/* Summary stat tiles — clickable to filter */}
        <div className="rpa-stat-row">
          <button
            className={`rpa-stat-card rpa-stat-card--progress ${statusFilter === 'in_progress' ? 'rpa-stat-card--active' : ''}`}
            onClick={() => handleTileClick('in_progress')}
          >
            <span className="rpa-stat-card__count">{stats.inProgress}</span>
            <span className="rpa-stat-card__label">In Progress</span>
          </button>
          <button
            className={`rpa-stat-card rpa-stat-card--success ${statusFilter === 'success' ? 'rpa-stat-card--active' : ''}`}
            onClick={() => handleTileClick('success')}
          >
            <span className="rpa-stat-card__count">{stats.succeeded}</span>
            <span className="rpa-stat-card__label">Succeeded</span>
          </button>
          <button
            className={`rpa-stat-card rpa-stat-card--failed ${statusFilter === 'error' ? 'rpa-stat-card--active' : ''}`}
            onClick={() => handleTileClick('error')}
          >
            <span className="rpa-stat-card__count">{stats.errored}</span>
            <span className="rpa-stat-card__label">Failed</span>
          </button>
          <button
            className={`rpa-stat-card rpa-stat-card--timeout ${statusFilter === 'timeout' ? 'rpa-stat-card--active' : ''}`}
            onClick={() => handleTileClick('timeout')}
          >
            <span className="rpa-stat-card__count">{stats.timedOut}</span>
            <span className="rpa-stat-card__label">Timed Out</span>
          </button>
        </div>

        {/* Filters */}
        <div className="rpa-filters">
          <div className="rpa-toggle-group">
            <button
              className={`rpa-toggle-btn ${typeFilter === 'all' ? 'rpa-toggle-btn--active' : ''}`}
              onClick={() => setTypeFilter('all')}
            >All</button>
            <button
              className={`rpa-toggle-btn ${typeFilter === 'revolution' ? 'rpa-toggle-btn--active' : ''}`}
              onClick={() => setTypeFilter('revolution')}
            >RevolutionEHR</button>
            {RPA_VENDORS.map(vendor => (
              <button
                key={vendor.id}
                className={`rpa-toggle-btn ${typeFilter === vendor.id ? 'rpa-toggle-btn--active' : ''}`}
                onClick={() => setTypeFilter(vendor.id)}
              >{vendor.displayName}</button>
            ))}
          </div>

          <div className="rpa-toggle-group rpa-toggle-group--right">
            {(Object.keys(DATE_PRESET_LABELS) as DatePreset[]).map(k => (
              <button
                key={k}
                className={`rpa-toggle-btn ${datePreset === k ? 'rpa-toggle-btn--active' : ''}`}
                onClick={() => setDatePreset(k)}
              >
                {DATE_PRESET_LABELS[k]}
              </button>
            ))}
          </div>
        </div>


        {/* Loading */}
        {loading && <p className="rpa-dashboard__loading">Loading jobs...</p>}

        {/* Empty state */}
        {!loading && filteredJobs.length === 0 && (
          <p className="rpa-dashboard__empty">No data syncs found for this period.</p>
        )}

        {/* Job table — proper <td> per column for alignment */}
        {!loading && filteredJobs.length > 0 && (
          <div className="rpa-table-wrapper">
            <table className="rpa-table">
              <thead>
                <tr>
                  <th className={thClass('jobType')} onClick={() => handleSort('jobType')}>Job Type{sortArrow('jobType')}</th>
                  <th className={thClass('status')} onClick={() => handleSort('status')}>Status{sortArrow('status')}</th>
                  <th>Context</th>
                  <th className={thClass('initiatedAt')} onClick={() => handleSort('initiatedAt')}>Initiated{sortArrow('initiatedAt')}</th>
                  <th className={thClass('completedAt')} onClick={() => handleSort('completedAt')}>Completed{sortArrow('completedAt')}</th>
                  <th className={thClass('durationMs')} onClick={() => handleSort('durationMs')}>Duration{sortArrow('durationMs')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedJobs.map(job => {
                  const isExpanded = expandedJobId === job.id
                  const hasDetail = (job.status === 'error' || job.status === 'timeout') && job.error
                  const ctx = job.context
                  const clinicName = clinicsById.get(job.clinicId)?.name ?? job.clinicId

                  return (
                    <Fragment key={job.id}>
                      <tr className={hasDetail ? 'rpa-table__row--expandable' : undefined}>
                        <td>
                          <JobTypeBadge job={job} />
                        </td>
                        <td>
                          <div className="rpa-table__status-cell">
                            <StatusBadge status={job.status} />
                            {hasDetail && (
                              <button
                                className="rpa-expand-btn"
                                onClick={() => toggleExpand(job)}
                                aria-label={isExpanded ? 'Collapse detail' : 'Expand detail'}
                              >
                                {isExpanded ? <IconChevronDown /> : <IconChevronRight />}
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          {ctx.kind === 'eligibility' ? (
                            <>
                              <div className="rpa-table__cell-primary">{ctx.patientName || 'Unknown Patient'}</div>
                              <div className="rpa-table__cell-secondary">
                                {ctx.dob ? `DOB: ${formatDob(ctx.dob)}` : ''}
                                {ctx.dob && ctx.payerName ? ' \u00b7 ' : ''}
                                {stripPayerIdSuffix(ctx.payerName ?? '')}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="rpa-table__cell-primary">{clinicName}</div>
                              <div className="rpa-table__cell-secondary">
                                Appts: {ctx.appointmentDate} &middot; {ctx.patientCount} patients
                              </div>
                            </>
                          )}
                        </td>
                        <td className="rpa-table__date-cell">{job.initiatedAt ? formatDateTime(job.initiatedAt) : '\u2014'}</td>
                        <td className="rpa-table__date-cell">{job.completedAt ? formatDateTime(job.completedAt) : '\u2014'}</td>
                        <td className="rpa-table__duration-cell">
                          {job.durationMs != null ? formatDuration(job.durationMs) : '\u2014'}
                        </td>
                        <td>
                          {canRerun(job) && (
                            <button
                              className="rpa-rerun-btn"
                              disabled={rerunningIds.has(job.id)}
                              onClick={() => handleRerun(job)}
                            >
                              <IconRefresh />
                              {rerunningIds.has(job.id) ? 'Running...' : job.jobType === 'revolution' ? 'Re-run' : 'Retry Check'}
                            </button>
                          )}
                        </td>
                      </tr>

                      {/* Expandable error detail row */}
                      {isExpanded && hasDetail && (
                        <tr className="rpa-table__detail-row">
                          <td colSpan={7}>
                            <div className="rpa-error-detail">
                              <span className="rpa-error-detail__label">Error Detail</span>
                              <p className="rpa-error-detail__text">{scrubVendorText(humanizeSyncError(job.error))}</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="rpa-pagination">
                <button
                  className="rpa-pagination__btn"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >Previous</button>
                <span className="rpa-pagination__info">
                  Page {page + 1} of {totalPages} ({filteredJobs.length} jobs)
                </span>
                <button
                  className="rpa-pagination__btn"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                >Next</button>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  )
}
